import { Component, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import {
  AgCustomPaginationComponent
} from "@shared/components/grid/components/ag-custom-pagination/ag-custom-pagination.component";
import { BaseGridComponent } from "@shared/components/grid/components/base-grid/base-grid.component";
import { PageSizeService } from "@shared/services/page-size.service";
import { AgGridAngular } from "ag-grid-angular";
import {
  ClientSideRowModelModule,
  FilterChangedEvent,
  GridApi,
  GridReadyEvent,
  IServerSideDatasource,
  ModuleRegistry,
  type PaginationChangedEvent,
  SelectionChangedEvent,
  StateUpdatedEvent
} from "ag-grid-community";
import { ColDef } from "ag-grid-enterprise";
import { BulkChangeData } from "@models/ui-models/bulk-change-data";
import { ColDefCommunityExtended, FilterOption, GridFilterModel } from "@models/grid/grid-filter";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import { SearchInputComponent } from "@shared/controls/search-input/search-input.component";
import { GridColumnsFilterComponent } from "../grid-columns-filter/grid-columns-filter.component";
import {
  GridServerColumnSearchFilterComponent
} from "../grid-server-column-search-filter/grid-server-column-search-filter.component";

interface DetailRendererParams {
  detailGridOptions: { columnDefs: ColDefCommunityExtended[] };
}

@Component({
  selector: 'app-grid-server-side',
  standalone: true,
  imports: [
    FormsModule,
    GridColumnsFilterComponent,
    LoaderComponent,
    ReactiveFormsModule,
    SearchInputComponent,
    AgCustomPaginationComponent,
    AgGridAngular,
    GridServerColumnSearchFilterComponent
  ],
  providers: [{ provide: PageSizeService, useClass: PageSizeService }],
  templateUrl: 'grid-server-side.component.html',
  styleUrl: 'grid-server-side.component.scss'
})
export class GridServerSideComponent extends BaseGridComponent implements OnInit {
  @Input({ required: true }) override colDefs: ColDefCommunityExtended[] = [];
  @Input() serverSideDatasource: IServerSideDatasource | undefined;
  @Input() cacheBlockSize = 20;
  @Input() maxBlocksInCache: number | undefined;

  readonly defaultPageSize = PageSizeService.defaultPageSize;

  columnDefs: ColDef[] = [];
  currentPage = 0;
  totalPages = 0;
  pageNumbers: (number | string)[] = [];
  gridApi!: GridApi;
  detailColDefs: ColDefCommunityExtended[] = [];
  rowDataCopy: unknown[] = [];

  ngOnInit(): void {
    ModuleRegistry.registerModules([ClientSideRowModelModule]);

    this.form.controls.search.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(search => this.search.emit(search ?? ''));

    if (this.pinFirstColumnName) {
      this.gridOptions = {
        ...(this.gridOptions || {}),
        onColumnMoved: () => this.ensureColumnAtFirst()
      };
    }

    if ((this.detailCellRendererParams as DetailRendererParams)?.detailGridOptions?.columnDefs) {
      this.detailColDefs = (this.detailCellRendererParams as DetailRendererParams).detailGridOptions.columnDefs;

      if (!this.rowDataCopy.length) {
        this.rowDataCopy = JSON.parse(JSON.stringify(this.rowData)) as unknown[];
      }
    }
  }

  ensureColumnAtFirst() {
    const currentState = this.gridApi.getColumnState();
    const index = currentState.findIndex(col => col.colId === this.pinFirstColumnName);
    if (index === -1 || index === 0) {
      return;
    }
    const newState = [...currentState];
    const [colState] = newState.splice(index, 1);

    newState.unshift(colState);

    this.gridApi.applyColumnState({
      state: newState,
      applyOrder: true
    });
  }

  getSavedColumnState(): any[] | null {
    const columnState = this.storageKey ? localStorage.getItem(this.storageKey) : null;
    return columnState ? JSON.parse(columnState) : null;
  }

  onCheckboxChange(colDef: ColDef): void {
    this.colDefs = this.colDefs.map((c) => {
      c.hide = c.field === colDef.field ? !c.hide : c.hide;
      return c;
    });

    this.gridApi?.setColumnsVisible([colDef.field as unknown as string], !colDef.hide);
  }

  onGroupCheckboxChange(colDef: ColDefCommunityExtended): void {
    this.colDefs = this.colDefs.map((c) => {
      return c?.groupId === colDef.groupId ? colDef : c;
    });

    colDef?.children?.forEach(c => {
      if (c.field) {
        this.gridApi?.setColumnsVisible([c.field], !colDef.hide);
      }
    });
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
    this.initFilter();
    this.gridReady.emit(event.api);
  }

  onStateUpdated(params: StateUpdatedEvent<any>): void {
    this.saveColumnState();
  }

  saveColumnState(): void {
    const storageKey = this.storageKey;
    if (storageKey) {
      const columnState = this.gridApi.getColumnState();
      localStorage.setItem(storageKey, JSON.stringify(columnState));
    }
  }

  onSelectionChanged(event: SelectionChangedEvent): void {
    const selectedRows = event.api.getSelectedRows();
    this.select.emit(selectedRows);

    //TODO refactor, should be check in upper components

    const isHeaderSource = event.source === 'uiSelectAll';

    if (isHeaderSource) {
      const selectedRows = event.api.getSelectedRows();
      const selectAllRows = (selectedRows as BulkChangeData[])?.length > 0;
      this.selectAll.emit(selectAllRows);
    }
  }

  onPaginationChanged(params: PaginationChangedEvent): void {
    if (this.paginationPageSize) {
      this.totalPages = params.api.paginationGetTotalPages();
      this.pageNumbers = this.getPageNumbers();
      this.cacheBlockSize = this.paginationPageSize;
    }
  }

  getPageNumbers(): (number | string)[] {
    const pageNumbers: (number | string)[] = [];
    const maxPagesToShow = 5;

    if (this.totalPages <= maxPagesToShow) {
      for (let i = 1; i <= this.totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const halfMaxPagesToShow = Math.floor(maxPagesToShow / 2);
      const startPage = Math.max(1, this.currentPage - halfMaxPagesToShow);
      const endPage = Math.min(this.totalPages, this.currentPage + halfMaxPagesToShow);

      if (startPage > 1) {
        pageNumbers.push(1, '...');
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < this.totalPages) {
        pageNumbers.push('...', this.totalPages);
      }
    }

    return pageNumbers;
  }

  onSelectAll(value: boolean): void {
    this.selectAll.emit(value);
  }

  initFilter(): void {
    const initialFilterModel: GridFilterModel = {};
    const savedState = this.getSavedColumnState();

    this.colDefs = this.colDefs.map((colDef: ColDefCommunityExtended) => {
      const field = colDef.field ?? '';
      const options: FilterOption[] = colDef.filterParams?.filterOptions;
      const activeOptions = options?.filter(option => option?.active);

      if (savedState && field) {
        const columnState = savedState.find((state: { colId: string }) => state.colId === field);

        if (columnState) {
          colDef.hide = columnState.hide;
        }
      }

      if (activeOptions?.length) {
        colDef.filterTagShowed = true;
        initialFilterModel[field] = { filterType: 'set', values: activeOptions.map(option => option.value) };
      } else {
        colDef.filterTagShowed = false;
      }

      return colDef;
    });

    this.gridApi?.setFilterModel(initialFilterModel);
  }

  onFilterChanged(event: FilterChangedEvent): void {
    this.totalPages = event.api.paginationGetTotalPages();
    this.currentPage = event.api.paginationGetCurrentPage();
    this.pageNumbers = this.getPageNumbers();
  }

  goToPreviousPage(): void {
    this.gridApi.paginationGoToPreviousPage();
    this.currentPage = this.gridApi.paginationGetCurrentPage();
    this.updatePageNumbers(this.currentPage + 1);
  }

  goToNextPage(): void {
    this.gridApi.paginationGoToNextPage();
    this.currentPage = this.gridApi.paginationGetCurrentPage();
    this.updatePageNumbers(this.currentPage + 1);
  }

  goToPage(page: number): void {
    this.gridApi.paginationGoToPage(page);
    this.currentPage = this.gridApi.paginationGetCurrentPage();
    this.updatePageNumbers(page + 1);
  }

  updatePageNumbers(pageNumber: number): void {
    if (pageNumber >= 1 && pageNumber <= this.totalPages) {
      this.pageNumbers = this.getPageNumbers();
    }
  }
}
